import { AfterViewInit, Directive, ElementRef, Input, OnDestroy, SimpleChanges } from '@angular/core';
import { MatFormField } from '@angular/material/form-field';
import { Subject, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { takeUntil } from 'rxjs/operators';
import { AppState } from 'src/app/app.reducer';
import { NgrxEagerSelectService } from '../../select/eager-select/ngrx-eager-select-status.service';
import { LcsSingleSelectDirective } from '../../single-select/single-select.directive';
import { PhoneNumberOption } from '../models/phone-number-option';
import { phoneNumberOptionActions } from '../state/phone-number-option.actions';
import { phoneNumberOptionSelectors } from '../state/phone-number-option.reducer';

@Directive({
    selector: '[ndtPhoneNumberSelect]',
    standalone: false
})
export class PhoneNumberSelectInputDirective implements OnDestroy, AfterViewInit {
   @Input() accountId: number | null;

   @Input() additionalFields: string[] | null;

   @Input() urlOverride?: string;

   @Input() onlyShowNoAccountPhoneNumbers?: boolean;

   private destroyed$ = new Subject<void>();
   
   private initializeSub?: Subscription;

   constructor(
      private elementRef: ElementRef,
      private formField: MatFormField,
      private trigger: LcsSingleSelectDirective<PhoneNumberOption>,
      private statusService: NgrxEagerSelectService<AppState, PhoneNumberOption>,
      protected store: Store<AppState>
   ) {}

   ngAfterViewInit(): void {
      this.runInitialize();
   }

   ngOnChanges(changes: SimpleChanges): void{
      if (changes.accountId || changes.onlyShowNoAccountPhoneNumbers) {
         this.store.dispatch(
            phoneNumberOptionActions.load({
               additionalFields: this.additionalFields,
               filterOverrides: this.buildFilters(),
               urlOverride: this.urlOverride,
            })
         );
         this.runInitialize()
      }
   }

   runInitialize(): void {
      this.initializeSub?.unsubscribe();
      this.initializeSub = this.statusService
         .initialize(
            this.formField,
            this.trigger,
            this.elementRef.nativeElement,
            phoneNumberOptionActions,
            phoneNumberOptionSelectors,
            () => this.additionalFields,
            () => this.urlOverride,
            () => this.statusService.buildPrimaryKeyFilterExpression('phoneNumberId', this.trigger),
            this.buildFilters()
          )
         .pipe(takeUntil(this.destroyed$))
         .subscribe();
   }
   
   buildFilters(): string[] {
      const filters: string[] = [];
      if (this.accountId) {
         filters.push(`AccountId = ${this.accountId}`);
      }
      if (this.onlyShowNoAccountPhoneNumbers === true && !this.accountId) {
         filters.push('AccountId IS NULL');
      }
      return filters;
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
      this.initializeSub?.unsubscribe();
   }
}
